// Position Tracker — periodic spoken price/P&L check-ins for open trades,
// while the app tab stays open. Sir explicitly asked for this ("mala trade
// milala tar... pratek 5 min la... sound track karata yeyil ka") — covers
// BOTH JARVIS's own auto-detected demo trades AND anything Sir manually
// added in the Journal with his own target/SL. Off by default — this is
// opt-in (Settings → Notifications), since polling every 5 min costs
// battery/data Sir should knowingly accept, not something silently always
// running.
//
// Reliability note (told to Sir directly, repeating here for whoever reads
// this code later): this only works while the JARVIS tab is open. It works
// BEST with the screen on. With the screen off, Android throttles/pauses
// background tabs — the Media Session "keep-alive" below (a silent,
// perpetual audio stream + real MediaSession metadata) gets Chrome to treat
// this tab more like a music-player tab, which meaningfully improves
// background reliability, but it is NOT the same guarantee a true native
// app's foreground service gets. If Chrome itself gets closed/swiped away,
// or aggressive battery-saver kicks in, this stops — that's a real
// platform ceiling, not a bug here.

import { getTrades } from './storage';
import { run as getLivePrice } from '../a-agents/trading/A_LivePrice';
import { speak, isMuted } from './voiceService';

const PREFS_KEY = 'jarvis_position_tracker_prefs';
export const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes, as asked

export const getTrackerPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || 'null') || { enabled: false }; }
  catch { return { enabled: false }; }
};

export const saveTrackerPrefs = (prefs) =>
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));

// ── Media Session keep-alive ────────────────────────────────────────────
// A near-silent, endlessly-looping audio stream, generated in-memory (no
// asset file needed) via Web Audio API, routed into a real <audio>
// element. Registering real MediaSession metadata on top of an actively
// "playing" audio element is what gets Chrome/Android to grant this tab
// the same background leniency a music-player website gets — this is a
// real, documented browser behavior, not a hack that happens to work.
let _audioCtx = null;
let _audioEl  = null;

export function startKeepAlive() {
  if (_audioEl) return; // already running
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    _audioCtx = new Ctx();
    const dest = _audioCtx.createMediaStreamDestination();
    const osc  = _audioCtx.createOscillator();
    const gain = _audioCtx.createGain();
    gain.gain.value = 0.00001; // effectively silent
    osc.connect(gain);
    gain.connect(dest);
    osc.start();

    _audioEl = new Audio();
    _audioEl.srcObject = dest.stream;
    _audioEl.loop = true;
    _audioEl.play().catch(() => { /* needs a user gesture — the Settings toggle click provides one */ });

    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: 'JARVIS Position Tracker',
        artist: 'XAU/USD — live tracking active',
        album: 'JARVIS Trading AI',
      });
      navigator.mediaSession.playbackState = 'playing';
    }
  } catch { /* Web Audio unavailable — tracker still works, just without the background boost */ }
}

export function stopKeepAlive() {
  try { _audioEl?.pause(); } catch { /* ignore */ }
  try { _audioCtx?.close(); } catch { /* ignore */ }
  _audioEl = null;
  _audioCtx = null;
  if ('mediaSession' in navigator) {
    try { navigator.mediaSession.playbackState = 'none'; navigator.mediaSession.metadata = null; } catch { /* ignore */ }
  }
}

// ── The actual check ─────────────────────────────────────────────────────
function describeTrade(t, price) {
  const isBuy = t.direction === 'BUY';
  const pnl = isBuy ? price - t.entry : t.entry - price;
  const toTarget = isBuy ? t.tp1 - price : price - t.tp1; // <=0 means price has already reached/passed tp1
  const toSL = isBuy ? price - t.sl : t.sl - price;
  const state = pnl >= 0 ? `profit of ${Math.abs(pnl).toFixed(1)} dollars` : `loss of ${Math.abs(pnl).toFixed(1)} dollars`;
  const nearSL = t.sl && toSL > 0 && toSL < Math.abs(t.entry - t.sl) * 0.25;
  const targetPart = !t.tp1 ? '' : toTarget <= 0
    ? `, target already reached — by ${Math.abs(toTarget).toFixed(1)} dollars`
    : `, target ${toTarget.toFixed(1)} dollars away`;
  const slWarning = nearSL ? '. Stop loss जवळ आहे, सांभाळा' : '';
  return `XAU/USD ${t.direction} trade — price ${price.toFixed(1)}, ${state}${targetPart}${slWarning}.`;
}

export async function checkPositionsAndSpeak() {
  if (isMuted()) return; // respects the existing mute toggle
  const open = getTrades().filter((t) => t.result === 'OPEN');
  if (open.length === 0) return; // nothing to report — stays silent

  const priceResult = await getLivePrice();
  if (!priceResult.ok || !priceResult.price) return;
  const price = priceResult.price;

  // Stale-source warning — real money is being tracked here, so a frozen
  // source price (found live 2026-08-23: Yahoo's GC=F feed can freeze for
  // 1-2 days) must never be spoken as if it's current without saying so.
  const staleWarning = priceResult.stale
    ? `Warning — price data ${priceResult.dataAgeMinutes >= 60 ? (priceResult.dataAgeMinutes / 60).toFixed(1) + ' hours' : Math.round(priceResult.dataAgeMinutes) + ' minutes'} जुनं आहे, live नसेल. `
    : '';

  const lines = open.map((t) => describeTrade(t, price));
  speak(`Sir, position update. ${staleWarning}${lines.join(' ')}`);
}
